/* eslint-disable no-param-reassign */
import jwtDecode from 'jwt-decode';
import {
  postLogin, postRecoverPassword, updatePassword, postRegister, userVerification,
  refreshToken, verifyToken,
} from '../requests/api/auth';
import axios from '../utils/axios';
import {
  UNKNOWN_ERROR,

} from '../utils/const/errors';

const getAccessToken = () => localStorage.getItem('accessToken');
const TOKEN_REFRESH_THRESHOLD_SECONDS = Number(
  process.env.REACT_APP_JWT_REFRESH_THRESHOLD_SECONDS,
) || 2 * 60 * 60;
let refreshPromise = null;

const getTokenTimeRemaining = (accessToken) => {
  if (!accessToken) {
    return null;
  }

  try {
    const { exp } = jwtDecode(accessToken);
    if (!exp) {
      return null;
    }

    return exp - (Date.now() / 1000);
  } catch {
    return null;
  }
};

const isValidToken = (accessToken) => {
  const timeRemaining = getTokenTimeRemaining(accessToken);
  return timeRemaining !== null && timeRemaining > 0;
};

const shouldRefreshToken = (accessToken) => {
  const timeRemaining = getTokenTimeRemaining(accessToken);
  return timeRemaining !== null
    && timeRemaining > 0
    && timeRemaining <= TOKEN_REFRESH_THRESHOLD_SECONDS;
};

const setSession = async (accessToken, companyId) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    delete axios.defaults.headers.common.Authorization;
  }

  if (companyId) {
    localStorage.setItem('companyId', companyId);
    axios.defaults.params = {};
    axios.defaults.params.companyId = companyId;
  }
};

const refreshUserToken = async () => {
  try {
    const response = await refreshToken({ skipTokenRefresh: true });
    if (response.success) {
      setSession(response.data.token, null);
      return {
        success: true,
      };
    }
    return {
      success: false,
      errorMessage: UNKNOWN_ERROR,
    };
  } catch (err) {
    return {
      success: false,
      errorMessage: UNKNOWN_ERROR,
    };
  }
};

const refreshAccessToken = async () => {
  if (!refreshPromise) {
    refreshPromise = refreshUserToken().finally(() => {
      refreshPromise = null;
    });
  }

  const response = await refreshPromise;
  return response.success;
};

const setAxiosInterceptors = ({ onLogout }) => {
  // Varias requests concurrentes pueden compartir el mismo refreshPromise y fallar a la vez,
  // asi que deduplicamos el logout dentro del turno del event loop en el que se dispara.
  let logoutInProgress = false;

  const logout = () => {
    if (logoutInProgress) {
      return;
    }
    logoutInProgress = true;
    setTimeout(() => {
      logoutInProgress = false;
    }, 0);

    setSession(null, null);
    if (onLogout) {
      onLogout();
    }
  };

  // Request interceptor para verificar y renovar el token antes de cada request
  axios.interceptors.request.use(
    async (config) => {
      const accessToken = getAccessToken();
      if (accessToken && !isValidToken(accessToken)) {
        logout();
        return Promise.reject(new Error('Token expired'));
      }

      if (accessToken && !config.skipTokenRefresh && shouldRefreshToken(accessToken)) {
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          logout();
          return Promise.reject(new Error('Unable to refresh token'));
        }

        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${getAccessToken()}`;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor para manejar errores 401
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && error.response.status === 401) {
        logout();
      }

      return Promise.reject(error);
    },
  );
};

// eslint-disable-next-line no-unused-vars
const login = async (email, password, remember) => {
  const ipAddress = 'No user ip';

  // Temporarily disabled because the service appears to be unresponsive or disabled
  // try {
  //   // eslint-disable-next-line no-undef
  //   const res = await axios.get('https://geolocation-db.com/json/');
  //   ipAddress = (res.data.IPv4);
  // } catch {
  //   ipAddress = 'No user ip';
  // }
  const userAgentFull = window.navigator.userAgent;
  const userAgentShort = 'No user agent short';

  try {
    const response = await postLogin({
      email,
      password,
      ipAddress,
      userAgentFull,
      userAgentShort,
    });

    let user;
    let token;
    if (response.data.success) {
      ({ user, token } = response.data.data);
      setSession(token, null);
    } else {
      return {
        success: false,
        errorMessage: response.data.errorMessage,
      };
    }
    return {
      user,
      success: true,
    };
  } catch (err) {
    return {
      success: false,
      errorMessage: UNKNOWN_ERROR,
    };
  }
};

const loginInWithToken = async () => {
  const accessToken = getAccessToken();
  const decoded = jwtDecode(accessToken);

  return decoded.user;
};

const logout = () => {
  setSession(null, null);
};

const handleAuthentication = () => {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return;
  }

  if (isValidToken(accessToken)) {
    setSession(accessToken, null);
  } else {
    setSession(null, null);
  }
};

const isAuthenticated = () => !!getAccessToken();

// RECOVER PASSWORD
const recoverPassword = async (email) => {
  try {
    const response = await postRecoverPassword(email);
    if (response.data.success) {
      return {
        success: true,
      };
    }
    return {
      success: false,
      errorMessage: response.message,
    };
  } catch (err) {
    return {
      success: false,
      errorMessage: UNKNOWN_ERROR,
    };
  }
};

// RESET PASSWORD
const resetPassword = async (token, password, confirmPassword) => {
  try {
    const response = await updatePassword({
      token,
      password,
      confirmPassword,
    });
    if (response.success) {
      return {
        success: true,
      };
    }
    return {
      success: false,
      errorMessage: response.message,
    };
  } catch (err) {
    return {
      success: false,
      errorMessage: UNKNOWN_ERROR,
    };
  }
};

const register = async (fullName, dni, countryCode, email, customPassword, role, phoneNumber,
  numberAccount, qrAuthorization, avatar, lastName, bank,
) => {
  const password = customPassword || Math.random().toString(36).slice(-8);

  try {
    const response = await postRegister({
      fullName,
      dni,
      countryCode,
      email,
      password,
      role,
      phoneNumber,
      numberAccount,
      qrAuthorization,
      avatar,
      lastName,
      bank,
    });

    if (response.success) {
      return {
        success: true,
      };
    }
    return {
      success: false,
      errorMessage: response.message,
    };
  } catch (err) {
    return {
      success: false,
      errorMessage: UNKNOWN_ERROR,
    };
  }
};

// USER VERIFICATION
const verification = async (token, password, confirmPassword) => {
  try {
    const response = await userVerification({
      token,
      password,
      confirmPassword,
    });
    if (response.success) {
      return {
        success: true,
      };
    }
    return {
      success: false,
      errorMessage: response.message,
    };
  } catch (err) {
    return {
      success: false,
      errorMessage: UNKNOWN_ERROR,
    };
  }
};

const verifyTokenValidity = async () => {
  try {
    const response = await verifyToken();
    return {
      success: response.success,
      valid: response.success && response.data.success,
    };
  } catch (err) {
    return {
      success: false,
      valid: false,
    };
  }
};

export default {
  setAxiosInterceptors,
  login,
  loginInWithToken,
  logout,
  handleAuthentication,
  isAuthenticated,
  recoverPassword,
  resetPassword,
  register,
  verification,
  setSession,
  verifyTokenValidity,
};
